import {
  ExecutionDoneData,
  ExecutionErrorData,
  ExecutionPlanEventData,
  ExecutionStepEventData,
  ExecutionStreamChunk,
  ExecutionStreamEventType,
  ExecutionSynthesisChunkData,
  ExecutionToolResultData,
  ExecutionTraceState,
  ExecutionTraceToolResult,
  ExecutionTraceStep,
  ExecutionCostSnapshot,
} from '@shared/orchestration';

const createInitialState = (): ExecutionTraceState => ({
  steps: {},
  stepOrder: [],
  toolResults: [],
  synthesisChunks: [],
  errors: [],
  isStreaming: false,
  completed: false,
});

const mergeCostSnapshot = (
  current: ExecutionCostSnapshot | undefined,
  incoming: ExecutionCostSnapshot | undefined,
): ExecutionCostSnapshot | undefined => {
  if (!incoming && !current) return undefined;
  if (!incoming) return current;
  if (!current) return { ...incoming };
  const merged: ExecutionCostSnapshot = {
    ...current,
    ...incoming,
    visible:
      typeof incoming.visible === 'boolean'
        ? incoming.visible
        : current.visible,
  };

  const mergeRecord = (
    target: Record<string, number> | undefined,
    addition: Record<string, number> | undefined,
  ) => {
    if (!addition) return target;
    const next = { ...(target ?? {}) };
    for (const [key, value] of Object.entries(addition)) {
      next[key] = value;
    }
    return next;
  };

  merged.perStepUsd = mergeRecord(current.perStepUsd, incoming.perStepUsd);
  merged.perToolUsd = mergeRecord(current.perToolUsd, incoming.perToolUsd);
  merged.perPhaseUsd = mergeRecord(current.perPhaseUsd, incoming.perPhaseUsd);
  merged.breakdown = mergeRecord(current.breakdown, incoming.breakdown);
  if (typeof incoming.totalUsd === 'number') {
    merged.totalUsd = incoming.totalUsd;
  }
  if (typeof incoming.percentUsed === 'number') {
    merged.percentUsed = incoming.percentUsed;
  }
  if (typeof incoming.budgetUsd === 'number') {
    merged.budgetUsd = incoming.budgetUsd;
  }
  if (incoming.currency) {
    merged.currency = incoming.currency;
  }
  return merged;
};

export class ExecutionTraceStore {
  private state: ExecutionTraceState = createInitialState();
  private processedEventIds = new Set<string>();

  reset() {
    this.state = createInitialState();
    this.processedEventIds.clear();
  }

  getState(): Readonly<ExecutionTraceState> {
    // Return a shallow copy so React detects state changes
    // (React uses Object.is comparison which requires new references)
    return { ...this.state };
  }

  getLastEventId(): string | undefined {
    return this.state.lastEventId;
  }

  applyChunk(chunk: ExecutionStreamChunk): boolean {
    if (!chunk || !chunk.id) return false;
    if (this.processedEventIds.has(chunk.id)) {
      this.state.lastEventId = chunk.id;
      return false;
    }

    this.processedEventIds.add(chunk.id);
    this.state.lastEventId = chunk.id;
    this.state.lastUpdatedAt = chunk.timestamp ?? Date.now();

    switch (chunk.type as ExecutionStreamEventType) {
      case 'plan':
        return this.handlePlan(chunk.data as ExecutionPlanEventData);
      case 'step-start':
      case 'step-end':
        return this.handleStepEvent(chunk.data as ExecutionStepEventData, chunk.type);
      case 'tool-result':
        return this.handleToolResult(chunk.id, chunk.data as ExecutionToolResultData);
      case 'synthesis-chunk':
        return this.handleSynthesis(chunk.data as ExecutionSynthesisChunkData);
      case 'error':
        return this.handleError(chunk.data as ExecutionErrorData);
      case 'done':
        return this.handleDone(chunk.data as ExecutionDoneData);
      default:
        return false;
    }
  }

  private handlePlan(data: ExecutionPlanEventData): boolean {
    if (!data?.plan) return false;
    this.state.plan = data.plan;
    this.state.correlationId = data.correlationId ?? this.state.correlationId;
    this.state.requestId = data.requestId ?? this.state.requestId;
    this.state.degradation = data.degradation ?? this.state.degradation;
    this.state.cost = mergeCostSnapshot(this.state.cost, data.cost);
    if (data.costVisibility === 'hidden') {
      this.state.cost = {
        ...(this.state.cost ?? {
          totalUsd: 0,
          currency: 'USD',
        }),
        visible: false,
      };
    }
    this.state.isStreaming = true;
    return true;
  }

  private ensureStep(stepId: string, payload?: ExecutionStepEventData): ExecutionTraceStep {
    if (!this.state.steps[stepId]) {
      const tools = payload?.tools ?? [];
      const step: ExecutionTraceStep = {
        id: stepId,
        label: payload?.label,
        status: payload?.status ?? 'pending',
        startedAt: payload?.startedAt,
        completedAt: payload?.completedAt,
        durationMs: payload?.durationMs,
        tools,
        metadata: payload?.metadata,
      };
      this.state.steps[stepId] = step;
      if (!this.state.stepOrder.includes(stepId)) {
        this.state.stepOrder = [...this.state.stepOrder, stepId];
      }
    }

    const step = this.state.steps[stepId];
    if (payload?.tools?.length) {
      step.tools = payload.tools;
    }
    if (payload?.metadata) {
      step.metadata = { ...(step.metadata ?? {}), ...payload.metadata };
    }
    if (payload?.label) {
      step.label = payload.label;
    }
    return step;
  }

  private handleStepEvent(
    data: ExecutionStepEventData,
    eventType: ExecutionStreamEventType,
  ): boolean {
    if (!data?.stepId) return false;
    const step = this.ensureStep(data.stepId, data);
    if (eventType === 'step-start') {
      step.status = data.status ?? 'running';
      step.startedAt = data.startedAt ?? Date.now();
      step.metadata = { ...(step.metadata ?? {}), ...(data.metadata ?? {}) };
      return true;
    }

    step.status = data.status ?? step.status;
    step.completedAt = data.completedAt ?? Date.now();
    if (typeof data.durationMs === 'number') {
      step.durationMs = data.durationMs;
    } else if (step.startedAt && step.completedAt) {
      step.durationMs = step.completedAt - step.startedAt;
    }
    step.metadata = { ...(step.metadata ?? {}), ...(data.metadata ?? {}) };
    return true;
  }

  private handleToolResult(
    eventId: string,
    data: ExecutionToolResultData,
  ): boolean {
    if (!data?.toolName || !data?.metadata?.stepId) {
      return false;
    }

    const record: ExecutionTraceToolResult = {
      id: eventId,
      toolName: data.toolName,
      status: data.status,
      payload: data.payload,
      isPartial: data.isPartial,
      isFinal: data.isFinal,
      metadata: data.metadata,
    };

    const existingIndex = this.state.toolResults.findIndex(
      (item) => item.id === eventId,
    );
    if (existingIndex >= 0) {
      const next = [...this.state.toolResults];
      next[existingIndex] = record;
      this.state.toolResults = next;
    } else {
      this.state.toolResults = [...this.state.toolResults, record];
    }

    // Update step metadata/status if necessary
    const step = this.ensureStep(data.metadata.stepId);
    if (data.status === 'partial' && step.status !== 'failure') {
      step.status = 'partial';
    } else if (data.isFinal && data.status === 'failed') {
      step.status = 'failure';
    } else if (data.isFinal && data.status === 'success' && step.status !== 'failure') {
      step.status = 'success';
    } else if (!data.isFinal) {
      step.status = 'running';
    }

    // Track cost increments
    if (typeof data.metadata.costUsd === 'number') {
      const existingCost = this.state.cost ?? {
        totalUsd: 0,
        currency: 'USD',
      };
      const perTool = existingCost.perToolUsd ?? {};
      const currentToolTotal = perTool[data.toolName] ?? 0;
      perTool[data.toolName] = currentToolTotal + data.metadata.costUsd;
      const perStep = existingCost.perStepUsd ?? {};
      const currentStepTotal = perStep[data.metadata.stepId] ?? 0;
      perStep[data.metadata.stepId] = currentStepTotal + data.metadata.costUsd;
      this.state.cost = {
        ...existingCost,
        totalUsd: (existingCost.totalUsd ?? 0) + data.metadata.costUsd,
        perToolUsd: perTool,
        perStepUsd: perStep,
      };
    }

    return true;
  }

  private handleSynthesis(data: ExecutionSynthesisChunkData): boolean {
    // The server wraps synthesis data in nested structure: { data: { content: '...' } }
    // Handle both direct content and nested content
    let chunkData: ExecutionSynthesisChunkData;
    
    // Use typeof check instead of truthiness to handle empty string content
    // (e.g., final marker events have content: '' with isFinal: true)
    if (typeof data?.content === 'string') {
      // Direct content structure
      chunkData = data;
    } else if (typeof (data as any)?.data?.content === 'string') {
      // Nested content structure from server - spread to capture all fields
      // (tokens, latencyMs, correlationId, requestId, etc.)
      const nested = (data as any).data as ExecutionSynthesisChunkData;
      chunkData = { ...nested };
    } else {
      // No valid content found
      return false;
    }
    
    this.state.synthesisChunks = [...this.state.synthesisChunks, chunkData];
    return true;
  }

  private handleError(data: ExecutionErrorData): boolean {
    if (!data) return false;
    this.state.errors = [...this.state.errors, data];
    if (!data.recoverable) {
      this.state.isStreaming = false;
    }
    if (data.context?.step) {
      const step = this.state.steps[data.context.step];
      if (step) {
        step.status = 'failure';
      }
    }
    return true;
  }

  private handleDone(data: ExecutionDoneData): boolean {
    this.state.completed = true;
    this.state.isStreaming = false;
    if (data?.finalCost) {
      this.state.cost = mergeCostSnapshot(this.state.cost, data.finalCost);
    }
    this.state.correlationId = data?.correlationId ?? this.state.correlationId;
    this.state.requestId = data?.requestId ?? this.state.requestId;
    return true;
  }

  dump(): ExecutionTraceState {
    return JSON.parse(JSON.stringify(this.state)) as ExecutionTraceState;
  }
}
